// HomeChoice.jsx
// URL: /   — Landing page. Students choose their path here.
//   First time   -> /request          (existing CertificatePortal form)
//   Re-download  -> /my-certificate   (self-service page)

import { useNavigate } from "react-router-dom";

export default function HomeChoice() {
  const navigate = useNavigate();

  return (
    <div style={S.page}>
      <header style={S.header}>
        <div style={S.logo}>🎓 Uncodemy</div>
        <p style={S.tagline}>Certificate Portal</p>
      </header>

      <h1 style={S.h1}>What would you like to do?</h1>
      <p style={S.lead}>Choose the option that matches your situation</p>

      <div style={S.grid}>

        {/* ── Option 1: First time ── */}
        <button
          style={{ ...S.card, ...S.cardPrimary }}
          onClick={() => navigate("/request")}
          onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-4px)")}
          onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
        >
          <div style={S.icon}>📝</div>
          <div style={S.badgeNew}>FIRST TIME</div>
          <h2 style={S.cardTitle}>Request a New Certificate</h2>
          <p style={S.cardText}>
            You haven&apos;t applied for your certificate yet. Fill in the request
            form and you will receive a download link by email once the
            administrator approves it.
          </p>
          <ul style={S.list}>
            <li>Your course should be completed</li>
            <li>Use your registered email address</li>
            <li>Approval usually takes 1–2 working days</li>
          </ul>
          <span style={S.cta}>Fill Request Form →</span>
        </button>

        {/* ── Option 2: Re-download ── */}
        <button
          style={{ ...S.card, ...S.cardSecondary }}
          onClick={() => navigate("/my-certificate")}
          onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-4px)")}
          onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
        >
          <div style={S.icon}>📥</div>
          <div style={S.badgeOld}>ALREADY APPROVED</div>
          <h2 style={S.cardTitle}>Download Your Certificate Again</h2>
          <p style={S.cardText}>
            Your certificate is already approved. Just enter your email address
            and download it instantly — there is no limit on how many times you
            can download it.
          </p>
          <ul style={S.list}>
            <li>Email download link expired?</li>
            <li>Lost or deleted your certificate?</li>
            <li>Available as PDF or PNG</li>
          </ul>
          <span style={{ ...S.cta, color: "#2d3542" }}>Enter Email &amp; Download →</span>
        </button>
      </div>

      {/* ── Extra links ── */}
      <div style={S.extraRow}>
        <a href="/webinar" style={S.extraLink}>🎥 Webinar Certificate</a>
        <span style={S.dot}>•</span>
        <a href="mailto:admin@uncodemy.com" style={S.extraLink}>✉️ Help &amp; Support</a>
      </div>

      <div style={S.verifyNote}>
        🔍 Need to <strong>verify</strong> a certificate? Scan the QR code printed
        on the certificate — the verification page will open automatically.
      </div>

      <footer style={S.footer}>© Uncodemy Edutech Pvt. Ltd.</footer>
    </div>
  );
}

const S = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #f0f4ff 0%, #fff5f0 100%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "44px 16px",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    boxSizing: "border-box",
  },
  header: { textAlign: "center", marginBottom: 26 },
  logo: { fontSize: 30, fontWeight: 700, color: "#ff5421" },
  tagline: { color: "#64748b", fontSize: 14, marginTop: 4 },
  h1: { fontSize: 26, fontWeight: 700, color: "#1e1b4b", margin: "0 0 6px", textAlign: "center" },
  lead: { color: "#64748b", fontSize: 15, margin: "0 0 32px", textAlign: "center" },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: 20,
    width: "100%",
    maxWidth: 840,
  },
  card: {
    background: "#fff",
    border: "2px solid #e2e8f0",
    borderRadius: 16,
    padding: "28px 26px",
    textAlign: "left",
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "transform 0.18s ease, box-shadow 0.18s ease",
    boxShadow: "0 4px 20px rgba(30,27,75,0.07)",
    display: "flex",
    flexDirection: "column",
  },
  cardPrimary:   { borderColor: "#ffd7c8" },
  cardSecondary: { borderColor: "#cbd5e1" },
  icon: { fontSize: 40, marginBottom: 10 },
  badgeNew: {
    display: "inline-block", alignSelf: "flex-start",
    background: "#fff1eb", color: "#c2410c",
    fontSize: 11, fontWeight: 800, letterSpacing: "0.08em",
    padding: "4px 10px", borderRadius: 20, marginBottom: 10,
  },
  badgeOld: {
    display: "inline-block", alignSelf: "flex-start",
    background: "#e0f2fe", color: "#075985",
    fontSize: 11, fontWeight: 800, letterSpacing: "0.08em",
    padding: "4px 10px", borderRadius: 20, marginBottom: 10,
  },
  cardTitle: { fontSize: 19, fontWeight: 700, color: "#1e1b4b", margin: "0 0 10px", lineHeight: 1.35 },
  cardText:  { color: "#64748b", fontSize: 14, lineHeight: 1.65, margin: "0 0 14px" },
  list: { margin: "0 0 18px", paddingLeft: 18, color: "#94a3b8", fontSize: 13, lineHeight: 1.9 },
  cta:  { marginTop: "auto", color: "#ff5421", fontSize: 14, fontWeight: 700 },
  extraRow: {
    display: "flex", alignItems: "center", gap: 12,
    marginTop: 28, flexWrap: "wrap", justifyContent: "center",
  },
  extraLink: { color: "#64748b", fontSize: 14, textDecoration: "none", fontWeight: 600 },
  dot: { color: "#cbd5e1" },
  verifyNote: {
    marginTop: 22, maxWidth: 560, textAlign: "center",
    background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10,
    padding: "12px 18px", color: "#64748b", fontSize: 13, lineHeight: 1.7,
  },
  footer: { color: "#94a3b8", fontSize: 12, marginTop: 26, textAlign: "center" },
};